<template>
  <div class="input-wrapper login-wrapper" :class="{ 'focused': isFocused || hasValue }"
    @mouseenter="showClearIconMouseHold = true" @mouseleave="showClearIconMouseHold = false">
    <input id="login-input-elm" class="style-input" type="text" name="login" autocomplete="username"
      :value="modelValue" @input="handleInput"
      @focus="onFocus" @blur="onBlur" ref="inputRef" :style="inputStyle"/>

    <label class="floating-label" :class="{ 'active': isFocused || hasValue }">
      {{ $t('Login.Email') }}
    </label>

    <span class="search_buttons_container" v-if="showIcons">
      <img v-if="showClearIcon" :src="iconClosePath" width="16" height="16" class="pointer"
        @mouseenter="showClearHint = true" @mouseleave="showClearHint = false" @click="clearField" @mousedown.prevent="clearField"/>

      <div v-if="showClearHint" class="hint-container">
        <div class="hint-content-email">
          {{ $t('Login.ClearField') }}
        </div>
      </div>

      <img v-if="isValid && !error && !isFocused" :src="iconCheckPath" width="16" height="16" class="pointer" />
      <img v-if="(!isValid || error) && !isFocused" :src="iconInvalidPath" width="16" height="16" class="pointer"
        @mouseenter="showErrorHint = true" @mouseleave="showErrorHint = false" />

      <div v-if="showErrorHint" class="hint-container">
        <div class="hint-content-email">
          {{ error ? $t('Login.CheckEmail') : $t('Login.IncorrectEmail') }}
        </div>
      </div>
    </span>
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted, watch } from 'vue';

import '@/styles/inputs.scss';

export default {
  name: 'LoginInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    error: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'clear-error', 'clear-field'],
  setup(props: any, context: any) {
    const iconCheckPath = new URL('@/assets/image/icon-check.svg', import.meta.url).href;
    const iconClosePath = new URL('@/assets/image/icon-arrow.svg', import.meta.url).href;
    const iconInvalidPath = new URL('@/assets/image/icon-invalid.svg', import.meta.url).href;

    const inputRef = ref<HTMLInputElement>();
    const isFocused = ref(false);
    const showErrorHint = ref(false);
    const showClearHint = ref(false);
    const showClearIconMouseHold = ref(false);
    const hasValue = ref(false);

    const checkAutofill = () => {
      if (inputRef.value) {
        const isAutofilled = 
          inputRef.value.matches(':-webkit-autofill') ||
          inputRef.value.matches(':autofill') ||
          inputRef.value.value.length > 0;
        
        hasValue.value = isAutofilled || (props.modelValue && props.modelValue.length > 0);
      }
    };

    const showIcons = computed(() => {
      return isFocused.value || hasValue.value;
    });

    const showClearIcon = computed(() => {
      return (isFocused.value || showClearIconMouseHold.value) && props.modelValue.length > 0;
    });

    const isValid = computed(() => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(props.modelValue);
    });

    const inputStyle = computed(() => {
      let visibleIconsCount = 0;
      
      if (showClearIcon.value) visibleIconsCount++;
      if (isValid.value && !props.error) visibleIconsCount++;
      if ((!isValid.value && !isFocused.value) || props.error) visibleIconsCount++;
      
      const basePadding = 16; 
      const iconWidth = 16; 
      const iconGap = 4; 
      const additionalPadding = visibleIconsCount * (iconWidth + iconGap);
      
      return {
        paddingRight: `${basePadding + additionalPadding}px`
      };
    });

    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement;
      context.emit('update:modelValue', target.value);
      context.emit('clear-error');
    };

    const onFocus = () => {
      isFocused.value = true;
      showClearHint.value = false;
    };

    const onBlur = () => {
      setTimeout(() => {
        isFocused.value = false;
        showClearHint.value = false;
      }, 100);
    };

    const clearField = (event: Event) => {
      event.preventDefault();
      
      showClearHint.value = false;

      context.emit('update:modelValue', '');
      context.emit('clear-field');
      context.emit('clear-error');
      
      setTimeout(() => {
        if (inputRef.value) {
          isFocused.value = true;
          inputRef.value.focus();
        }
      }, 0);
    };

    watch(() => props.modelValue, (newValue) => {
      hasValue.value = newValue && newValue.length > 0;
    });

    watch(() => props.modelValue, (newValue) => {
      hasValue.value = newValue && newValue.length > 0;

      if (!newValue && props.error) {
        context.emit('clear-error');
      }
    });

    onMounted(() => {
      setTimeout(() => {
        checkAutofill();
      }, 100);
    });

    return {
      iconCheckPath,
      iconClosePath,
      iconInvalidPath,
      isFocused,
      inputRef,
      showErrorHint,
      showClearHint,
      showClearIconMouseHold,
      hasValue,
      showIcons,
      showClearIcon,
      isValid,
      inputStyle,
      handleInput,
      onFocus,
      onBlur,
      clearField
    };
  }
}
</script>

<style scoped>
.input-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;

  &:hover .style-input {
    border: 1px solid #3F884F;
  }
}

.login-wrapper.focused .style-input {
  padding-top: 8px;
}

.search_buttons_container {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  gap: 8px;
  align-items: center;
}

.hint-container {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  z-index: 1000;
}

.hint-content-email {
  background: #5D5D5D;
  opacity: 0.9;
  color: white;
  width: max-content;
  height: 20px;
  padding: 2px 8px;
  border-radius: 8px;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.pointer {
  cursor: pointer;
}

.floating-label {
  position: absolute;
  left: 12px;
  top: 22%;
  color: #9C9C9C;
  font-size: 14px;
  pointer-events: none;
  transition: all 0.3s ease;
  /* background: white; */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.floating-label.active {
  top: 1px;
  font-size: 10px;
  background: none;
}

@media (max-width: 768px) {
  .style-input {
    width: 100% !important;
  }

  .input-wrapper {
    width: 100%;
  }
}
</style>
